'use client';

import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { addByIncrement } from '../../store/reducers/cartSlice';
import { ClockIcon } from '@heroicons/react/24/solid';

export default function Modal({ isOpen, closeModal, data }) {
  const [total, setTotal] = useState(1);
  const dispatch = useDispatch();

  const language = useSelector((state) => state.language.language);

  const text = {
    minuteText: language === 'en' ? 'minutes' : 'मिनट',
    stockOutText: language === 'en' ? 'Stock Out' : 'स्टॉक खत्म',
    stockInText: language === 'en' ? 'In Stock' : 'स्टॉक में उपलब्ध',
    addCartText: language === 'en' ? 'Add To Cart' : 'कार्ट में डालें',
    categoryText: language === 'en' ? 'Category' : 'श्रेणी',
    serviceIncludeText: language === 'en' ? 'Service Include' : 'सेवा में शामिल',
  };

  const handleAddToCart = () => {
    dispatch(addByIncrement({ product: data, cartQuantity: total }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal Panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-90"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-90"
            >
              <Dialog.Panel className="relative w-full max-w-3xl transform overflow-hidden rounded-xl bg-white/90 shadow-xl ring-1 ring-gray-300 transition-all backdrop-blur-lg">
                {/* Close Button */}
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 z-10 text-gray-600 hover:text-red-500"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* Left: Image */}
                  <div className="bg-gradient-to-tr from-white to-gray-100 flex items-center justify-center p-5">
                    <Link href={`/service/${data?.parent_slug}/${data?.slug}`}>
                      <img
                        src={data.photo_url}
                        alt={data.title}
                        className="rounded-lg max-h-[300px] w-full object-contain shadow-sm transition-transform hover:scale-105"
                      />
                    </Link>
                  </div>

                  {/* Right: Content */}
                  <div className="p-6 flex flex-col justify-between space-y-4">
                    <div>
                      <div className="!mb-2 !md:mb-2.5 !block -mt-1.5 text-left">
  <h1 className="!text-heading !text-md md:!text-xl lg:!text-1xl !font-semibold !text-black !no-underline !cursor-pointer text-left">
    <Link 
      href={`/${data?.parent_name?.split(' ').join('-')}/${data?.parent}/${data?.product_name?.split(' ').join('-')}/${data?.id}`}
      className="!no-underline !text-black"
    >
      {data.title} {language === "en" ? data.product_name : data.product_name_hindi}
    </Link>
  </h1>

  {/* Stock status */}
  {data.quantity === 0 ? (
    <span className="bg-red-100 text-red-600 rounded-full inline-flex items-center justify-center px-2 py-1 text-xs font-semibold mt-2">
      {text.stockOutText}
    </span>
  ) : (
    <span className="bg-emerald-100 text-pink-600 rounded-full inline-flex items-center justify-center px-2 py-1 text-xs font-semibold mt-2">
      {text.stockInText}
    </span>
  )}
</div>


                      {/* Price */}
                      <div className="flex items-center space-x-2 mt-3 text-xl font-semibold text-black">
                        <span>₹{data.currentprice}</span>
                        {data.originalPrice !== data.currentprice && (
                          <del className="text-gray-400 text-base">₹{data.originalPrice}</del>
                        )}
                      </div>

                      {/* Duration */}
                      <div className="flex items-center text-gray-600 mt-2">
                        <ClockIcon className="w-5 h-5 text-pink-500 mr-1" />
                        <span>{data.time_duration} {text.minuteText}</span>
                      </div>

                      {/* Description */}
                      <div className="mt-4 max-h-[120px] overflow-y-auto text-sm text-gray-700 pr-1 text-left">
                        <p className="font-medium">{text.serviceIncludeText}:</p>
                        <div
                          className="mt-1 text-gray-600"
                          dangerouslySetInnerHTML={{
                            __html: (language === 'en' ? data.services : data.services_hindi) || '',
                          }}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-2">
                      <div className="flex items-center gap-4">
                        {/* Quantity */}
                        <div className="flex items-center border rounded-md overflow-hidden bg-white">
                          <button
                            onClick={() => setTotal(prev => Math.max(prev - 1, 1))}
                            className="px-3 py-2 text-lg text-gray-600 hover:bg-gray-200 disabled:opacity-30"
                            disabled={total <= 1}
                          >
                            -
                          </button>
                          <span className="px-4 font-semibold text-gray-700">{total}</span>
                          <button
                            onClick={() => setTotal(prev => prev + 1)}
                            className="px-3 py-2 text-lg text-gray-600 hover:bg-gray-200 disabled:opacity-30"
                            disabled={data.quantity === 0}
                          >
                            +
                          </button>
                        </div>

                        {/* Add to Cart */}
                        <button
  onClick={handleAddToCart}
  disabled={data.quantity === 0}
  className={`!flex-none !py-2.5 !px-4 !rounded-xl !text-white !font-semibold !text-xs !transition ${
    data.quantity === 0
      ? '!bg-gray-400 !cursor-not-allowed'
      : '!bg-pink-600 !hover:bg-emerald-600'
  }`}
>
  {text.addCartText}
</button>
                      </div>

                      {/* Category */}
                      <p className="mt-3 text-sm text-gray-500 text-left">
                        <strong>{text.categoryText}:</strong>{' '}
                        {language === 'en' ? data.type : data.type_hindi}
                      </p>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
